/**
 * GPU capability detection for the SystemCheck flow. Reads the WebGL renderer
 * string (UNMASKED_RENDERER_WEBGL) and matches it against a conservative
 * denylist of GPUs known to deliver an unstable viewer experience.
 *
 * Browser limits: VRAM and driver versions are not reliably exposed to JS, so
 * gating is renderer-string-only. Indirect VRAM-proxy signals are collected
 * separately by `modules/systemInfoAnalytics.ts` for analytics-only use.
 */

export type GpuCapabilityStatus = 'ok' | 'unsupported' | 'unknown';

export type GpuCapabilityResult = {
  status: GpuCapabilityStatus;
  renderer: string;
  vendor: string;
  /** GL_VERSION string, e.g. "WebGL 1.0 (OpenGL ES 2.0 Chromium)". Empty when
   *  the WebGL context could not be created. */
  glVersion: string;
  denylistMatch: Nullable<string>;
};

export type GpuDenylistEntry = {
  pattern: RegExp;
  reason: string;
};

/**
 * Conservative seed list of GPUs known to deliver an unstable Viewer
 * experience. Software renderers (SwiftShader / llvmpipe / Microsoft Basic
 * Render) are intentionally NOT on this list — they're handled by the
 * separate `hardwareAcceleration` check, which has its own remediation
 * ("turn HW accel back on in browser settings"). Extend as Datadog data
 * accumulates.
 */
export const GPU_DENYLIST: ReadonlyArray<GpuDenylistEntry> = [
  // Pre-Skylake-era Intel integrated graphics:
  //   - HD 2000–6999 (Sandy Bridge through Broadwell)
  //   - HD 510–530   (Skylake low-end)
  // UHD / Iris Xe / Iris Plus are intentionally not denylisted.
  {
    pattern: /\bIntel\b.*?\bHD Graphics (?:[2-6]\d{3}|5[0-3]\d)\b/i,
    reason: 'Pre-Skylake-era Intel HD Graphics',
  },

  // Westmere / Arrandale era — bare "HD Graphics" with no model number.
  {
    pattern: /\bIntel\b.*?\bHD Graphics(?!\s*\d)/i,
    reason: 'Very old Intel HD Graphics (no model number)',
  },
];

export type WebGLRendererInfo = {
  renderer: string;
  vendor: string;
  glVersion: string;
};

/**
 * Default WebGL reader. Returns empty strings if the WEBGL_debug_renderer_info
 * extension is unavailable or context creation fails — caller treats that as
 * `status: unknown` (fail open).
 */
export const readWebGLRendererInfo = (): WebGLRendererInfo => {
  let renderer = '';
  let vendor = '';
  let glVersion = '';

  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') ?? canvas.getContext('experimental-webgl');

    if (gl != null && 'getParameter' in gl) {
      const ctx = gl as WebGLRenderingContext;
      const debugInfo = ctx.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        renderer = String(ctx.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) ?? '');
        vendor = String(ctx.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) ?? '');
      }
      glVersion = String(ctx.getParameter(ctx.VERSION) ?? '');
      ctx.getExtension('WEBGL_lose_context')?.loseContext();
    }
    if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
  } catch {
    // Empty renderer surfaces as `status: unknown` upstream.
  }

  return { renderer, vendor, glVersion };
};

const matchDenylist = (renderer: string): Nullable<string> => {
  for (const entry of GPU_DENYLIST) {
    if (entry.pattern.test(renderer)) return entry.reason;
  }
  return null;
};

/**
 * Reads the user's GPU and classifies it against the denylist. Accepts an
 * injected reader for tests; defaults to the real WebGL canvas reader.
 */
export const detectGpuCapability = (
  readRendererInfo: () => WebGLRendererInfo = readWebGLRendererInfo
): GpuCapabilityResult => {
  const { renderer, vendor, glVersion } = readRendererInfo();

  if (!renderer) {
    return {
      status: 'unknown',
      renderer: '',
      vendor: '',
      glVersion: glVersion ?? '',
      denylistMatch: null,
    };
  }

  const denylistMatch = matchDenylist(renderer);
  return {
    status: denylistMatch != null ? 'unsupported' : 'ok',
    renderer,
    vendor,
    glVersion,
    denylistMatch,
  };
};
